import { ApiResponse, Vehicle, Challan, Alert, ImportLog, DashboardSummary } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1';
const TENANT_ID = import.meta.env.VITE_TENANT_ID || 'demo_tenant';
const AUTH_TOKEN = import.meta.env.VITE_AUTH_TOKEN || 'dev-token';

async function fetchApi<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${endpoint}`;
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${AUTH_TOKEN}`,
    'X-Tenant-ID': TENANT_ID,
    ...options.headers,
  };
  try {
    const response = await fetch(url, { ...options, headers });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'API request failed');
    return data;
  } catch (error: any) {
    console.error('API Error:', error);
    throw error;
  }
}

export const getDashboardSummary = () => fetchApi<DashboardSummary>('/dashboard/summary');

export const getVehicles = (params?: { page?: number; limit?: number; status?: string; search?: string }) => {
  const sp = new URLSearchParams();
  if (params?.page) sp.set('page', params.page.toString());
  if (params?.limit) sp.set('limit', params.limit.toString());
  if (params?.status) sp.set('status', params.status);
  if (params?.search) sp.set('search', params.search);
  const q = sp.toString();
  return fetchApi<Vehicle[]>(`/vehicles${q ? '?' + q : ''}`);
};

export const getVehicle = (vehicleNumber: string) => fetchApi<Vehicle & { challans: Challan[] }>(`/vehicles/${encodeURIComponent(vehicleNumber)}`);
export const createVehicle = (data: Partial<Vehicle>) => fetchApi<Vehicle>('/vehicles', { method: 'POST', body: JSON.stringify(data) });
export const deleteVehicle = (vehicleNumber: string) => fetchApi<void>(`/vehicles/${encodeURIComponent(vehicleNumber)}`, { method: 'DELETE' });
export const blockVehicle = (vehicleNumber: string, blocked: boolean, reason: string) => fetchApi<void>(`/vehicles/${encodeURIComponent(vehicleNumber)}/block`, { method: 'POST', body: JSON.stringify({ blocked, reason }) });
export const fetchChallansForVehicle = (vehicleNumber: string) => fetchApi<any>(`/vehicles/${encodeURIComponent(vehicleNumber)}/fetch`, { method: 'POST' });

export const getChallans = (params?: { page?: number; limit?: number; status?: string; stateCode?: string; vehicleNumber?: string }) => {
  const sp = new URLSearchParams();
  if (params?.page) sp.set('page', params.page.toString());
  if (params?.limit) sp.set('limit', params.limit.toString());
  if (params?.status) sp.set('status', params.status);
  if (params?.stateCode) sp.set('stateCode', params.stateCode);
  if (params?.vehicleNumber) sp.set('vehicleNumber', params.vehicleNumber);
  const q = sp.toString();
  return fetchApi<Challan[]>(`/challans${q ? '?' + q : ''}`);
};
export const getChallan = (challanNumber: string) => fetchApi<Challan>(`/challans/${encodeURIComponent(challanNumber)}`);
export const getChallanStats = () => fetchApi<any>('/challans/stats');

export const getAlerts = (params?: { page?: number; limit?: number; status?: string }) => {
  const sp = new URLSearchParams();
  if (params?.page) sp.set('page', params.page.toString());
  if (params?.limit) sp.set('limit', params.limit.toString());
  if (params?.status) sp.set('status', params.status);
  const q = sp.toString();
  return fetchApi<Alert[]>(`/alerts${q ? '?' + q : ''}`);
};
export const acknowledgeAlert = (alertId: string) => fetchApi<void>(`/alerts/${alertId}/acknowledge`, { method: 'PUT' });
export const resolveAlert = (alertId: string) => fetchApi<void>(`/alerts/${alertId}/resolve`, { method: 'PUT' });

export const uploadCSV = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`${API_BASE}/import/csv`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${AUTH_TOKEN}`, 'X-Tenant-ID': TENANT_ID },
    body: formData,
  });
  return response.json();
};
export const getImportHistory = () => fetchApi<ImportLog[]>('/import/history');
export const downloadTemplate = async () => {
  const response = await fetch(`${API_BASE}/import/template/download`, {
    headers: { 'Authorization': `Bearer ${AUTH_TOKEN}`, 'X-Tenant-ID': TENANT_ID },
  });
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'challan_import_template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const getDrivers = (params?: { page?: number; limit?: number; status?: string; search?: string }) => {
  const sp = new URLSearchParams();
  if (params?.page) sp.set('page', params.page.toString());
  if (params?.limit) sp.set('limit', params.limit.toString());
  if (params?.status) sp.set('status', params.status);
  if (params?.search) sp.set('search', params.search);
  const q = sp.toString();
  return fetchApi<any[]>(`/drivers${q ? '?' + q : ''}`);
};
export const getDriver = (licenseNumber: string) => fetchApi<any>(`/drivers/${encodeURIComponent(licenseNumber)}`);
export const createDriver = (data: any) => fetchApi<any>('/drivers', { method: 'POST', body: JSON.stringify(data) });
export const deleteDriver = (licenseNumber: string) => fetchApi<any>(`/drivers/${encodeURIComponent(licenseNumber)}`, { method: 'DELETE' });
export const getDriverStats = () => fetchApi<any>('/drivers/stats');
